import React, { useState } from 'react';
import { Newspaper, Calendar, User, CheckCircle, XCircle, FileText, ExternalLink } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
interface NewsItem {
  id: string;
  title: string;
  content: string;
  author: string;
  date: string;
  category: 'company' | 'hr' | 'team' | 'event';
  hasRSVP?: boolean;
  eventDate?: string;
  eventLocation?: string;
  documentLink?: {
    label: string;
    url: string;
    isExternal?: boolean;
  };
}
const mockNews: NewsItem[] = [{
  id: '1',
  title: 'Nouvelle politique de télétravail',
  content: "À partir du 1er janvier, tous les employés pourront bénéficier de 2 jours de télétravail par semaine. Cette nouvelle politique vise à améliorer l'équilibre vie professionnelle-vie personnelle.",
  author: 'Direction RH',
  date: '2023-12-10',
  category: 'hr',
  documentLink: {
    label: 'Consulter la politique complète',
    url: '/documents',
    isExternal: false
  }
}, {
  id: '2',
  title: "Fête de fin d'année",
  content: "Rejoignez-nous le 20 décembre pour notre fête de fin d'année annuelle. Repas, activités et surprises au programme!",
  author: 'Comité social',
  date: '2023-12-08',
  category: 'event',
  hasRSVP: true,
  eventDate: '20 décembre 2023',
  eventLocation: 'Salle de réception - Bâtiment principal'
}, {
  id: '3',
  title: 'Nouveau projet en cours',
  content: "L'équipe technique lance un nouveau projet innovant qui transformera notre façon de travailler. Plus de détails à venir prochainement.",
  author: 'Direction technique',
  date: '2023-12-05',
  category: 'company',
  documentLink: {
    label: 'Voir la présentation du projet',
    url: 'https://example.com/projet-presentation.pdf',
    isExternal: true
  }
}, {
  id: '4',
  title: 'Formation disponible',
  content: "De nouvelles formations en leadership et gestion d'équipe sont maintenant disponibles. Inscrivez-vous dès maintenant!",
  author: 'Formation & Développement',
  date: '2023-12-03',
  category: 'hr',
  documentLink: {
    label: 'Voir le catalogue de formations',
    url: '/documents',
    isExternal: false
  }
}, {
  id: '5',
  title: "BBQ d'été de l'équipe",
  content: "Venez célébrer l'été avec nous! BBQ, jeux et détente au programme. Amenez votre famille!",
  author: 'Comité social',
  date: '2023-12-01',
  category: 'event',
  hasRSVP: true,
  eventDate: '15 juillet 2024',
  eventLocation: 'Parc municipal'
}];
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'company':
      return 'bg-blue-100 text-blue-800';
    case 'hr':
      return 'bg-purple-100 text-purple-800';
    case 'team':
      return 'bg-green-100 text-green-800';
    case 'event':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'company':
      return 'Entreprise';
    case 'hr':
      return 'RH';
    case 'team':
      return 'Équipe';
    case 'event':
      return 'Événement';
    default:
      return 'Autre';
  }
};
export function NewsFeed() {
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();
  const [responses, setResponses] = useState<Record<string, 'yes' | 'no' | null>>({});
  const handleRSVP = (newsId: string, response: 'yes' | 'no') => {
    setResponses({
      ...responses,
      [newsId]: response
    });
    showToast(response === 'yes' ? 'Votre présence a été confirmée ✓' : 'Votre absence a été enregistrée', 'success');
  };
  const handleDocumentClick = (link: {
    url: string;
    isExternal?: boolean;
  }) => {
    if (link.isExternal) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.url);
    }
  };
  return <div className="pb-20 md:pb-0" data-id="element-807">
      <div className="mb-6" data-id="element-808">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-809">2x4</h1>
        <p className="text-gray-600" data-id="element-810">
          Restez informé des dernières actualités de l'entreprise
        </p>
      </div>

      <div className="space-y-4" data-id="element-811">
        {mockNews.map(news => {
        const userResponse = responses[news.id];
        return <div key={news.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow" data-id="element-812">
              <div className="flex items-start justify-between mb-3" data-id="element-813">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getCategoryColor(news.category)}`} data-id="element-814">
                  {getCategoryLabel(news.category)}
                </span>
                <div className="flex items-center text-sm text-gray-500" data-id="element-815">
                  <Calendar className="h-4 w-4 mr-1" data-id="element-816" />
                  {new Date(news.date).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
                </div>
              </div>

              <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-817">
                {news.title}
              </h2>

              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-818">
                {news.content}
              </p>

              {news.hasRSVP && <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-819">
                  <div className="mb-3" data-id="element-820">
                    <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-821">
                      📅 Date: {news.eventDate}
                    </p>
                    <p className="text-sm text-blue-800" data-id="element-822">
                      📍 Lieu: {news.eventLocation}
                    </p>
                  </div>

                  {userResponse ? <div className={`flex items-center gap-2 p-3 rounded-lg ${userResponse === 'yes' ? 'bg-green-100' : 'bg-gray-100'}`} data-id="element-823">
                      {userResponse === 'yes' ? <>
                          <CheckCircle className="h-5 w-5 text-green-600" data-id="element-824" />
                          <span className="text-sm font-medium text-green-900" data-id="element-825">
                            Vous avez confirmé votre présence
                          </span>
                        </> : <>
                          <XCircle className="h-5 w-5 text-gray-600" data-id="element-826" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-827">
                            Vous avez indiqué votre absence
                          </span>
                        </>}
                      <button onClick={() => setResponses({
                ...responses,
                [news.id]: null
              })} className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline" data-id="element-828">
                        Modifier
                      </button>
                    </div> : <div data-id="element-829">
                      <p className="text-sm font-semibold text-gray-900 mb-3" data-id="element-830">
                        Allez-vous être avec nous?
                      </p>
                      <div className="flex gap-3" data-id="element-831">
                        <button onClick={() => handleRSVP(news.id, 'yes')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-832">
                          <CheckCircle className="h-5 w-5" data-id="element-833" />
                          Oui, je serai présent(e)
                        </button>
                        <button onClick={() => handleRSVP(news.id, 'no')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors" data-id="element-834">
                          <XCircle className="h-5 w-5" data-id="element-835" />
                          Non, je serai absent(e)
                        </button>
                      </div>
                    </div>}
                </div>}

              {news.documentLink && <div className="mb-4" data-id="element-836">
                  <button onClick={() => handleDocumentClick(news.documentLink!)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium transition-colors border border-blue-200" data-id="element-837">
                    <FileText className="h-4 w-4" data-id="element-838" />
                    {news.documentLink.label}
                    {news.documentLink.isExternal && <ExternalLink className="h-4 w-4 ml-1" data-id="element-839" />}
                  </button>
                </div>}

              <div className="flex items-center text-sm text-gray-600" data-id="element-840">
                <User className="h-4 w-4 mr-1" data-id="element-841" />
                <span data-id="element-842">{news.author}</span>
              </div>
            </div>;
      })}
      </div>

      {mockNews.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center" data-id="element-843">
          <Newspaper className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-844" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-845">
            Aucune actualité
          </h3>
          <p className="text-gray-600" data-id="element-846">
            Il n'y a pas d'actualités pour le moment. Revenez plus tard!
          </p>
        </div>}
    </div>;
}